
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { MapPin, Loader2 } from 'lucide-react';
import { ChargingStation } from '@/types/task';
import { calculateDistance } from '@/utils/distance';

interface Props {
  stations: ChargingStation[];
  onStationSelect?: (station: ChargingStation) => void;
}

const NearbyStations = ({ stations, onStationSelect }: Props) => {
  const [, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [nearbyStations, setNearbyStations] = useState<ChargingStation[]>([]);

  const getCurrentLocation = () => {
    setLoading(true);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setUserLocation(location);
          updateNearbyStations(location);
          setLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLoading(false);
        }
      );
    }
  };

  const updateNearbyStations = (location: { latitude: number; longitude: number }) => {
    const stationsWithDistance = stations
      .filter(station => station.coordinates)
      .map(station => ({
        ...station,
        distance: calculateDistance(
          location.latitude,
          location.longitude,
          station.coordinates!.latitude,
          station.coordinates!.longitude
        )
      }))
      .sort((a, b) => ((a.distance || 0) - (b.distance || 0)))
      .slice(0, 5);

    setNearbyStations(stationsWithDistance);
  };

  return (
    <div className="space-y-4">
      <Button 
        onClick={getCurrentLocation} 
        variant="outline" 
        className="w-full"
        disabled={loading}
      >
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <MapPin className="mr-2 h-4 w-4" />
        )}
        Find Closest Stations
      </Button>

      {nearbyStations.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-medium">Nearby Stations</h3>
          <div className="grid gap-2">
            {nearbyStations.map((station) => (
              <button
                key={station.id}
                onClick={() => onStationSelect?.(station)}
                className="flex items-start justify-between p-3 bg-background rounded-lg border hover:bg-accent transition-colors"
              >
                <div className="flex-1">
                  <h4 className="font-medium">{station.title}</h4>
                  <p className="text-sm text-muted-foreground">{station.location}</p>
                </div>
                <span className="text-sm font-medium">
                  {(station as any).distance?.toFixed(1)} km
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NearbyStations;
